import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Filter, Search, Clock, User, Key, Wallet, Camera, 
  Shield, Settings, MessageSquare, Users, TrendingUp, TrendingDown,
  Calendar, Download, RefreshCw, ChevronDown, X, Activity,
  AlertTriangle, Lock, Eye, BarChart3, Zap
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { auditAPI } from '../../api';
import { format, parseISO, subDays } from 'date-fns';
import toast from 'react-hot-toast';

const ACTION_CATEGORIES = {
  all: { label: 'All Activities', icon: Activity, color: 'slate' },
  auth: { label: 'Authentication', icon: Shield, color: 'emerald' },
  api: { label: 'API Usage', icon: Camera, color: 'cyan' },
  wallet: { label: 'Wallet & Billing', icon: Wallet, color: 'blue' },
  team: { label: 'Team Management', icon: Users, color: 'purple' },
  settings: { label: 'Settings', icon: Settings, color: 'amber' },
  support: { label: 'Support', icon: MessageSquare, color: 'pink' }
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#6366f1'];

const getActionIcon = (action) => {
  if (action.includes('LOGIN') || action.includes('REGISTER') || action.includes('PASSWORD') || action.includes('PHONE') || action.includes('GOOGLE')) return Shield;
  if (action.includes('API_KEY')) return Key;
  if (action.includes('WALLET') || action.includes('SUBSCRIPTION') || action.includes('REFERRAL')) return Wallet;
  if (action.includes('TRYON') || action.includes('MOMENTS')) return Camera;
  if (action.includes('TEAM')) return Users;
  if (action.includes('PROFILE') || action.includes('SETTINGS') || action.includes('SHARE')) return Settings;
  if (action.includes('TICKET')) return MessageSquare;
  return FileText;
};

const getActionColor = (action) => {
  if (action.includes('LOGIN') || action.includes('REGISTER') || action.includes('PASSWORD') || action.includes('PHONE') || action.includes('GOOGLE')) return 'emerald';
  if (action.includes('API_KEY')) return 'amber';
  if (action.includes('WALLET') || action.includes('SUBSCRIPTION') || action.includes('REFERRAL')) return 'blue';
  if (action.includes('TRYON') || action.includes('MOMENTS')) return 'cyan';
  if (action.includes('TEAM')) return 'purple';
  if (action.includes('PROFILE') || action.includes('SETTINGS') || action.includes('SHARE')) return 'amber';
  if (action.includes('TICKET')) return 'pink';
  return 'slate';
};

const colorClasses = {
  emerald: 'text-emerald-600 bg-emerald-100',
  amber: 'text-amber-600 bg-amber-100',
  blue: 'text-blue-600 bg-blue-100',
  cyan: 'text-cyan-600 bg-cyan-100',
  purple: 'text-purple-600 bg-purple-100',
  pink: 'text-pink-600 bg-pink-100',
  slate: 'text-slate-600 bg-slate-100'
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    actionType: 'all',
    search: '',
    startDate: '',
    endDate: '',
    includeTeam: true
  });
  const [showFilters, setShowFilters] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [teamMembersCount, setTeamMembersCount] = useState(0);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
        include_team: filters.includeTeam
      };
      
      if (filters.actionType !== 'all') {
        params.action_type = filters.actionType;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.startDate) {
        params.start_date = filters.startDate;
      }
      if (filters.endDate) {
        params.end_date = filters.endDate;
      }
      
      const response = await auditAPI.getLogs(params);
      setLogs(response.data.logs || []);
      setTotalCount(response.data.count || 0);
      setTeamMembersCount(response.data.team_members_included || 0);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSummary = async () => {
    try {
      const response = await auditAPI.getSummary({ days: 30 });
      setSummary(response.data);
      if (response.data.category_counts) {
        setCategoryCounts(response.data.category_counts);
      }
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      actionType: 'all',
      search: '',
      startDate: '',
      endDate: '',
      includeTeam: true
    });
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Action', 'Actor', 'Actor Type', 'Details'].join(','),
      ...logs.map(log => [
        log.timestamp,
        log.action,
        log.actor_name || 'You',
        log.actor_type || 'owner',
        JSON.stringify(log.details || {}).replace(/,/g, ';')
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit logs exported');
  };

  const formatDetails = (details) => {
    if (!details || details === '{}') return null;
    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      return Object.entries(parsed)
        .filter(([_, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(' • ');
    } catch {
      return details;
    }
  };

  // Prepare chart data
  const dailyChartData = summary?.daily_activity 
    ? Object.entries(summary.daily_activity)
        .map(([date, count]) => ({ date: date.slice(5), count }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-14)
    : [];

  const hourlyChartData = summary?.hourly_distribution
    ? Object.entries(summary.hourly_distribution)
        .map(([hour, count]) => ({ hour: `${hour}:00`, count }))
    : [];

  const actionPieData = summary?.action_breakdown
    ? Object.entries(summary.action_breakdown)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }))
    : [];

  const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekdayChartData = summary?.weekday_distribution
    ? Object.entries(summary.weekday_distribution)
        .map(([day, count]) => ({ day: weekdayLabels[parseInt(day)] || day, count }))
    : [];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'timeline', label: 'Activity Timeline', icon: Clock },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Dashboard</h1>
          <p className="text-slate-500">Track all account activities, security events, and team actions</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { fetchLogs(); fetchSummary(); }}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={exportLogs}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="export-logs-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Activities</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.total_activities || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">Last {summary.period_days} days</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">This Week</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.recent_7_days || 0}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(summary.trend_percentage || 0) >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    {(summary.trend_percentage || 0) >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </div>
                <p className={`text-xs mt-2 ${(summary.trend_percentage || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(summary.trend_percentage || 0) >= 0 ? '+' : ''}{summary.trend_percentage || 0}% vs last week
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">API Activity</p>
                    <p className="text-2xl font-bold text-slate-900">{categoryCounts.api || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-cyan-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">TryOn & Moments calls</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Security Events</p>
                    <p className="text-2xl font-bold text-slate-900">{categoryCounts.auth || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">Logins & auth changes</p>
              </div>
            </div>
          )}

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Activity Chart */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Daily Activity (Last 14 Days)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#93c5fd" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Hourly Distribution */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Hourly Distribution (UTC)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hourlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 8 }} interval={2} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Activity by Type */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Top Activity Types</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={actionPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {actionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Weekday Distribution */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Activity by Day of Week</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weekdayChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Peak Hours */}
          {summary?.peak_hours?.length > 0 && (
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Peak Activity Hours</h3>
              <div className="flex gap-4">
                {summary.peak_hours.map((peak, index) => (
                  <div key={index} className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === 0 ? 'bg-amber-100 text-amber-700' : 
                      index === 1 ? 'bg-slate-200 text-slate-700' : 
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{peak.hour}:00 UTC</p>
                      <p className="text-xs text-slate-500">{peak.count} activities</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(ACTION_CATEGORIES).map(([key, { label, icon: Icon, color }]) => {
              const count = key === 'all' ? (summary?.total_activities || totalCount) : (categoryCounts[key] || 0);
              const isActive = filters.actionType === key;
              
              return (
                <button
                  key={key}
                  onClick={() => handleFilterChange('actionType', key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${key}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${isActive ? 'bg-blue-500' : 'bg-slate-200'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Advanced Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <Filter className="w-4 h-4" />
                <span className="font-medium">Advanced Filters</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              
              {(filters.search || filters.startDate || filters.endDate) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                  Clear filters
                </button>
              )}
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search actions..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="search-input"
                  />
                </div>

                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="start-date-input"
                  />
                </div>

                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="end-date-input"
                  />
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.includeTeam}
                    onChange={(e) => handleFilterChange('includeTeam', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    data-testid="include-team-checkbox"
                  />
                  <span className="text-sm text-slate-600">Include team activities</span>
                </label>
              </div>
            )}
          </div>

          {/* Logs List */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : logs.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {logs.map((log, index) => {
                  const Icon = getActionIcon(log.action);
                  const color = getActionColor(log.action);
                  const details = formatDetails(log.details);
                  
                  return (
                    <motion.div
                      key={log.log_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`log-entry-${index}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium text-slate-900">
                              {log.action.replace(/_/g, ' ')}
                            </p>
                            {log.actor_type === 'team_member' && (
                              <p className="text-xs text-purple-600 mt-0.5">
                                by {log.actor_name} ({log.actor_role})
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-slate-400 flex-shrink-0 whitespace-nowrap">
                            {log.timestamp ? format(parseISO(log.timestamp), 'MMM d, HH:mm') : '-'}
                          </span>
                        </div>
                        
                        {details && (
                          <p className="text-sm text-slate-500 mt-1 truncate">
                            {details}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <Clock className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No audit logs found</h3>
                <p className="text-slate-500">
                  {filters.actionType !== 'all' || filters.search 
                    ? 'Try adjusting your filters' 
                    : 'Activities will be logged as you use the platform'}
                </p>
              </div>
            )}
          </div>

          {/* Results Info */}
          {!loading && logs.length > 0 && (
            <div className="flex items-center justify-between text-sm text-slate-500">
              <p>
                Showing {logs.length} of {totalCount} activities
                {teamMembersCount > 0 && ` (including ${teamMembersCount} team members)`}
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Security Summary Cards */}
          {summary?.security_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`bg-white rounded-xl p-4 border ${summary.security_summary.failed_logins > 0 ? 'border-red-200' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Failed Logins</p>
                    <p className={`text-2xl font-bold ${summary.security_summary.failed_logins > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                      {summary.security_summary.failed_logins}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${summary.security_summary.failed_logins > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                    <AlertTriangle className={`w-5 h-5 ${summary.security_summary.failed_logins > 0 ? 'text-red-600' : 'text-slate-600'}`} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Password Changes</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.security_summary.password_changes}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">API Key Changes</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.security_summary.api_key_changes}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Key className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Team Changes</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.security_summary.team_changes}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Status */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Security Status</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Account Protection</p>
                    <p className="text-sm text-slate-500">Password-based authentication active</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">Active</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Audit Logging</p>
                    <p className="text-sm text-slate-500">All activities are being tracked</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">Enabled</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Key className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">API Key Security</p>
                    <p className="text-sm text-slate-500">Keys are encrypted and secure</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">Secure</span>
              </div>
            </div>
          </div>

          {/* Recent Security Events */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Recent Security Events</h3>
            
            <div className="space-y-3">
              {logs
                .filter(log => ['LOGIN', 'PASSWORD', 'API_KEY', 'TEAM', 'GOOGLE'].some(k => log.action.includes(k)))
                .slice(0, 10)
                .map((log, index) => {
                  const Icon = getActionIcon(log.action);
                  const color = getActionColor(log.action);
                  
                  return (
                    <div key={log.log_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm text-slate-800">{log.action.replace(/_/g, ' ')}</p>
                        {log.actor_email && <p className="text-xs text-slate-500">{log.actor_email}</p>}
                      </div>
                      <span className="text-xs text-slate-400">
                        {log.timestamp ? format(parseISO(log.timestamp), 'MMM d, HH:mm') : '-'}
                      </span>
                    </div>
                  );
                })}
              
              {logs.filter(log => ['LOGIN', 'PASSWORD', 'API_KEY', 'TEAM', 'GOOGLE'].some(k => log.action.includes(k))).length === 0 && (
                <p className="text-center py-8 text-slate-500">No security events in this period</p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AuditLogs;
